import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Globe } from 'lucide-react';
export function CoverageMap() {
  const locations = ['MCO Orlando', 'FLL Fort Lauderdale', 'MIA Miami', 'Barranquilla', 'Cartagena', 'Medellin', 'El Salvador'];
  // Map coordinates (relative percentages for positioning on the SVG viewBox)
  const dots = [{
    id: 'mia',
    cx: '78%',
    cy: '85%',
    label: 'MIA'
  }, {
    id: 'fll',
    cx: '79%',
    cy: '82%',
    label: 'FLL'
  }, {
    id: 'mco',
    cx: '76%',
    cy: '75%',
    label: 'MCO'
  }, {
    id: 'lax',
    cx: '15%',
    cy: '55%',
    label: 'LAX'
  }, {
    id: 'dfw',
    cx: '50%',
    cy: '65%',
    label: 'DFW'
  }, {
    id: 'jfk',
    cx: '85%',
    cy: '35%',
    label: 'JFK'
  }, {
    id: 'ord',
    cx: '65%',
    cy: '35%',
    label: 'ORD'
  }, {
    id: 'sea',
    cx: '15%',
    cy: '15%',
    label: 'SEA'
  }];
  return <section id="coverage" className="py-24 bg-navy relative border-b border-white/5 overflow-hidden" data-id="element-159">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-160">
        <div className="text-center mb-16 relative z-10" data-id="element-161">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4 uppercase" data-id="element-162">
            From Coast To Coast, We Deliver.
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto" data-id="element-163">
            Serving aircraft operators all over the United States and beyond.
          </p>
        </div>

        {/* Stylized Map Container */}
        <div className="relative w-[70%] mx-auto aspect-[16/9] mb-16" data-id="element-164">
          {/* Abstract US Map SVG Path */}
          <svg viewBox="0 0 1000 600" className="w-full h-full drop-shadow-2xl" preserveAspectRatio="xMidYMid meet" data-id="element-165">
            <path d="M150,100 L250,80 L400,120 L550,100 L700,150 L850,120 L900,200 L880,300 L920,400 L850,450 L750,550 L700,500 L600,520 L500,580 L400,500 L250,480 L150,400 L100,300 L80,200 Z" fill="#111827" stroke="#1f2937" strokeWidth="2" className="transition-all duration-1000" data-id="element-166" />

            {/* Connection Lines */}
            <defs data-id="element-167">
              <filter id="goldGlow" x="-50%" y="-50%" width="200%" height="200%" data-id="element-168">
                <feGaussianBlur stdDeviation="3" result="coloredBlur" data-id="element-169" />
                <feMerge data-id="element-170">
                  <feMergeNode in="coloredBlur" data-id="element-171" />
                  <feMergeNode in="SourceGraphic" data-id="element-172" />
                </feMerge>
              </filter>
            </defs>
            <motion.path d="M780,510 L790,492 L760,450 L500,390 L150,330" fill="none" stroke="#C9A84C" strokeWidth="2" strokeDasharray="6,4" filter="url(#goldGlow)" initial={{
            pathLength: 0,
            opacity: 0
          }} whileInView={{
            pathLength: 1,
            opacity: 0.9
          }} transition={{
            duration: 2,
            ease: 'easeInOut'
          }} data-id="element-173" />
            <motion.path d="M780,510 L850,210 L650,210 L150,90" fill="none" stroke="#C9A84C" strokeWidth="2" strokeDasharray="6,4" filter="url(#goldGlow)" initial={{
            pathLength: 0,
            opacity: 0
          }} whileInView={{
            pathLength: 1,
            opacity: 0.9
          }} transition={{
            duration: 2,
            delay: 0.5,
            ease: 'easeInOut'
          }} data-id="element-174" />
          </svg>

          {/* Map Dots */}
          {dots.map(dot => <div key={dot.id} className="absolute transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{
          left: dot.cx,
          top: dot.cy
        }} data-id="element-175">
              <div className="relative flex items-center justify-center" data-id="element-176">
                <span className="absolute w-6 h-6 rounded-full bg-gold/30 blur-md animate-pulse" data-id="element-177"></span>
                <motion.div className="relative w-3 h-3 bg-gold rounded-full" style={{
              boxShadow: '0 0 12px 4px rgba(201, 168, 76, 0.7), 0 0 24px 8px rgba(201, 168, 76, 0.35)'
            }} animate={{
              scale: [1, 1.5, 1],
              opacity: [1, 0.6, 1]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              delay: Math.random() * 2
            }} data-id="element-178" />
              </div>
              <span className="text-white/70 text-[10px] font-bold mt-1 tracking-wider hidden sm:block" data-id="element-179">
                {dot.label}
              </span>
            </div>)}
        </div>

        {/* Location Badges */}
        <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-12 relative z-10" data-id="element-180">
          {locations.map((loc, idx) => <div key={idx} className="bg-navy-light border border-white/10 rounded-full px-5 py-2 flex items-center hover:border-gold/50 transition-colors" data-id="element-181">
              <MapPin className="w-4 h-4 text-gold mr-2" data-id="element-182" />
              <span className="text-sm font-medium text-slate-300 uppercase tracking-wider" data-id="element-183">
                {loc}
              </span>
            </div>)}
        </div>

        {/* International Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-gradient-to-r from-gold-dark via-gold to-gold-dark rounded-2xl p-1 shadow-gold-glow max-w-4xl mx-auto relative z-10" data-id="element-184">
          <div className="bg-navy rounded-xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between text-center md:text-left" data-id="element-185">
            <div className="flex items-center mb-4 md:mb-0" data-id="element-186">
              <Globe className="w-8 h-8 text-gold mr-4 hidden sm:block" data-id="element-187" />
              <div data-id="element-188">
                <h4 className="text-white font-heading font-bold text-xl uppercase mb-1" data-id="element-189">
                  International Operations Available
                </h4>
                <p className="text-slate-400 text-sm" data-id="element-190">
                  Contact us for international transport and maintenance
                  inquiries.
                </p>
              </div>
            </div>
            <a href="#contact" className="bg-gold hover:bg-gold-light text-navy font-bold py-3 px-6 rounded-full transition-all whitespace-nowrap text-sm uppercase tracking-wide" data-id="element-191">
              Inquire Now
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}